import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FaBan, FaComments, FaExclamationTriangle, FaTicketAlt, FaUnlock } from 'react-icons/fa';
import { fraudGateService } from '../../../../services/fraudGateService';

export default function CommsAdminPanel() {
  const [tab, setTab] = useState('tickets');
  const [tickets, setTickets] = useState([]);
  const [bans, setBans] = useState([]);
  const [strikes, setStrikes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  const [chatOrderId, setChatOrderId] = useState('');
  const [chatMessages, setChatMessages] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [t, b, s] = await Promise.all([
        fraudGateService.adminListSupportTickets({ status: 'open' }),
        fraudGateService.adminListChatBans(),
        fraudGateService.adminListPhysicalContactStrikes({ pending: 'true' }),
      ]);
      setTickets(t);
      setBans(b);
      setStrikes(s);
    } catch (err) {
      toast.error(err.message || 'Failed to load comms admin data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const liftBan = async (banId) => {
    if (!window.confirm('Lift this chat ban early?')) return;
    try {
      setActingId(banId);
      await fraudGateService.adminLiftChatBan(banId);
      toast.success('Chat ban lifted');
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to lift ban');
    } finally {
      setActingId(null);
    }
  };

  const resolveStrike = async (strikeId, action) => {
    try {
      setActingId(strikeId);
      await fraudGateService.adminResolvePhysicalContactStrike(strikeId, action);
      toast.success(`Strike marked ${action}`);
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to resolve strike');
    } finally {
      setActingId(null);
    }
  };

  const loadOrderChat = async (e) => {
    e?.preventDefault();
    const id = chatOrderId.trim();
    if (!id) return;
    try {
      setActingId('chat');
      const data = await fraudGateService.adminListOrderChat(id);
      setChatMessages(data);
      if (!data.length) toast.info('No chat messages for this order');
    } catch (err) {
      toast.error(err.message || 'Could not load chat');
      setChatMessages([]);
    } finally {
      setActingId(null);
    }
  };

  const tabs = [
    { id: 'tickets', label: 'Support tickets', count: tickets.length, icon: FaTicketAlt },
    { id: 'bans', label: 'Chat bans', count: bans.length, icon: FaBan },
    { id: 'strikes', label: 'Package contact', count: strikes.length, icon: FaExclamationTriangle },
    { id: 'chat', label: 'Order chat', icon: FaComments },
  ];

  return (
    <section className="rounded-xl border border-indigo-200 bg-indigo-50/30 p-4 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-bold text-indigo-900">Communication rules — ops</h3>
        <button type="button" onClick={load} className="text-xs text-indigo-700 hover:underline">
          Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {tabs.map(({ id, label, count, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold ${
              tab === id
                ? 'bg-indigo-600 text-white'
                : 'bg-white border border-indigo-200 text-indigo-800'
            }`}
          >
            <Icon />
            {label}
            {count != null ? ` (${count})` : ''}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : tab === 'tickets' ? (
        tickets.length === 0 ? (
          <p className="text-sm text-slate-500">No open support tickets.</p>
        ) : (
          <div className="space-y-2">
            {tickets.map((t) => (
              <div key={t.ticketId} className="rounded-lg border bg-white px-3 py-2 text-sm">
                <p className="font-semibold text-slate-900">{t.subject}</p>
                <p className="text-xs text-slate-500 mt-1">
                  {t.category} · order {t.orderId || '—'} · {new Date(t.createdAt).toLocaleString('en-IN')}
                </p>
                {t.description && <p className="text-xs text-slate-600 mt-1">{t.description}</p>}
              </div>
            ))}
          </div>
        )
      ) : tab === 'bans' ? (
        bans.length === 0 ? (
          <p className="text-sm text-slate-500">No active chat bans.</p>
        ) : (
          <div className="space-y-2">
            {bans.map((b) => (
              <div
                key={b.banId}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-white px-3 py-2 text-sm"
              >
                <div>
                  <p className="font-semibold text-slate-900">
                    {b.userType} · {String(b.userId).slice(-6)}
                  </p>
                  <p className="text-xs text-slate-500">
                    Until {new Date(b.banExpiresAt).toLocaleString('en-IN')}
                    {b.orderId ? ` · order ${b.orderId}` : ''}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={actingId === b.banId}
                  onClick={() => liftBan(b.banId)}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline"
                >
                  <FaUnlock /> Lift ban
                </button>
              </div>
            ))}
          </div>
        )
      ) : tab === 'strikes' ? (
        strikes.length === 0 ? (
          <p className="text-sm text-slate-500">No strikes pending admin review.</p>
        ) : (
          <div className="space-y-2">
            {strikes.map((s) => (
              <div key={s.strikeId} className="rounded-lg border bg-white px-3 py-2 text-sm">
                <p className="font-semibold text-slate-900">
                  Strike #{s.strikeNumber} · seller {String(s.sellerId).slice(-6)}
                </p>
                <p className="text-xs text-slate-500">Order {s.orderId}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {['warned', 'suspended', 'cleared'].map((action) => (
                    <button
                      key={action}
                      type="button"
                      disabled={actingId === s.strikeId}
                      onClick={() => resolveStrike(s.strikeId, action)}
                      className="rounded border px-2 py-1 text-xs font-semibold capitalize hover:bg-slate-50"
                    >
                      {action}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <div className="space-y-3">
          <form onSubmit={loadOrderChat} className="flex gap-2">
            <input
              type="text"
              value={chatOrderId}
              onChange={(e) => setChatOrderId(e.target.value)}
              placeholder="Order ID"
              className="flex-1 rounded-lg border px-3 py-2 text-sm"
            />
            <button
              type="submit"
              disabled={actingId === 'chat'}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white"
            >
              Load chat
            </button>
          </form>
          <div className="max-h-64 overflow-y-auto space-y-2 rounded-lg border bg-white p-3">
            {chatMessages.map((m) => (
              <div
                key={m.messageId || m._id}
                className={`text-xs rounded px-2 py-1 ${
                  m.blocked || m.status === 'blocked'
                    ? 'bg-red-50 text-red-800 line-through'
                    : m.senderRole === 'system'
                      ? 'bg-slate-100 text-slate-600 italic'
                      : 'bg-slate-50 text-slate-800'
                }`}
              >
                <span className="font-semibold uppercase">{m.senderRole}</span>: {m.body}
                {m.blockReason && (
                  <span className="block text-red-600">Blocked: {m.blockReason}</span>
                )}
              </div>
            ))}
            {chatMessages.length === 0 && (
              <p className="text-xs text-slate-400 text-center py-4">Enter an order ID to review chat</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
